import { useEffect, useState, useCallback } from "react";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ROLE_ICONS, ROLE_COLORS } from "@/lib/roles";
import { RoleBadge } from "@/components/RoleBadge";
import { SectionTitle, Field, Empty } from "@/admin/ui";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const EMPTY = { label: "", color: "#f59e0b", icon: "crown", order: 0 };

export function AdminRoles() {
  const { refreshRoles } = useAuth();
  const [roles, setRoles] = useState(null);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => api.get("/admin/roles").then((r) => setRoles(r.data)).catch(() => setRoles([])), []);
  useEffect(() => { load(); }, [load]);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const body = { label: form.label, color: form.color, icon: form.icon, order: Number(form.order) || 0 };
      if (editing === "new") await api.post("/admin/roles", body); else await api.put(`/admin/roles/${editing}`, body);
      toast.success("Rütbe kaydedildi");
      setEditing(null); load(); refreshRoles();
    } catch (err) { toast.error(errMsg(err)); } finally { setSaving(false); }
  };
  const remove = async (r) => {
    if (!window.confirm(`"${r.label}" rütbesi silinsin mi? Bu rütbedeki kullanıcılar rütbesiz kalır.`)) return;
    try { await api.delete(`/admin/roles/${r.id}`); toast.success("Silindi"); load(); refreshRoles(); } catch (err) { toast.error(errMsg(err)); }
  };

  return (
    <div>
      <SectionTitle eyebrow="Moderasyon" title="Rütbeler">
        <Button data-testid="admin-add-role-button" onClick={() => { setForm({ ...EMPTY, order: (roles?.length || 0) + 1 }); setEditing("new"); }} className="cx-btn-primary rounded-xl h-10"><Plus className="h-4 w-4 mr-1" /> Yeni Rütbe</Button>
      </SectionTitle>
      <p className="text-sm text-slate-400 -mt-4 mb-6">İstediğin ismi, rengi ve ikonu (taç, kılıç, alev, elmas...) seç. Rütbeleri "Kullanıcılar" sekmesinden kullanıcılara ver; sohbette isminin yanında görünür.</p>
      {roles === null ? <div className="h-40 rounded-2xl bg-slate-900/60 animate-pulse" /> : roles.length === 0 ? <Empty text="Henüz rütbe yok." /> : (
        <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="admin-roles-list">
          {roles.map((r) => (
            <div key={r.id} data-testid="admin-role-item" className="cx-glass rounded-2xl p-5 flex flex-col gap-3" style={{ borderColor: `${r.color}55` }}>
              <RoleBadge role={r} size="lg" />
              <p className="text-xs text-slate-500">{r.user_count} kullanıcı · sıra #{r.order}</p>
              <div className="grid grid-cols-2 gap-2 mt-auto">
                <Button data-testid="admin-edit-role-button" variant="outline" onClick={() => { setForm({ ...r }); setEditing(r.id); }} className="h-9 rounded-lg bg-slate-900 border-slate-700 text-slate-200 hover:bg-slate-800"><Pencil className="h-3.5 w-3.5 mr-1" /> Düzenle</Button>
                <Button data-testid="admin-delete-role-button" variant="outline" onClick={() => remove(r)} className="h-9 rounded-lg bg-red-950/50 border-red-500/40 text-red-300 hover:bg-red-950"><Trash2 className="h-3.5 w-3.5 mr-1" /> Sil</Button>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="bg-slate-950/95 backdrop-blur-xl border-slate-800 text-slate-100 sm:max-w-lg" data-testid="admin-role-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">{editing === "new" ? "Yeni Rütbe" : "Rütbeyi Düzenle"}</DialogTitle>
            <DialogDescription className="text-slate-400">Önizleme anında güncellenir.</DialogDescription>
          </DialogHeader>
          <form onSubmit={save} className="space-y-4">
            <div className="rounded-xl bg-slate-900/60 border border-slate-800 p-4 flex items-center justify-center" data-testid="role-preview">
              <RoleBadge role={{ label: form.label || "RÜTBE ADI", color: form.color, icon: form.icon }} size="lg" />
            </div>
            <div className="grid grid-cols-[1fr_90px] gap-3">
              <Field label="Rütbe Adı"><Input data-testid="role-form-label" className="cx-input" value={form.label} onChange={(e) => setForm((f) => ({ ...f, label: e.target.value }))} placeholder="KRAL" required maxLength={40} /></Field>
              <Field label="Sıra"><Input data-testid="role-form-order" type="number" className="cx-input" value={form.order} onChange={(e) => setForm((f) => ({ ...f, order: e.target.value }))} /></Field>
            </div>
            <Field label="İkon">
              <div className="grid grid-cols-10 gap-1.5">
                {Object.entries(ROLE_ICONS).map(([k, Icon]) => (
                  <button type="button" key={k} data-testid={`role-icon-${k}`} onClick={() => setForm((f) => ({ ...f, icon: k }))} className={`h-9 rounded-lg border flex items-center justify-center transition-colors ${form.icon === k ? "border-cyan-400 bg-slate-800" : "border-slate-800 bg-slate-950 hover:border-slate-600"}`} style={{ color: form.icon === k ? form.color : "#94a3b8" }}>
                    <Icon className="h-4 w-4" />
                  </button>
                ))}
              </div>
            </Field>
            <Field label="Renk">
              <div className="flex gap-2 flex-wrap items-center">
                {ROLE_COLORS.map((c) => (
                  <button type="button" key={c} data-testid="role-color" onClick={() => setForm((f) => ({ ...f, color: c }))} className={`h-8 w-8 rounded-lg border-2 ${form.color === c ? "border-white" : "border-transparent"}`} style={{ backgroundColor: c }} />
                ))}
                <input type="color" data-testid="role-color-custom" value={form.color} onChange={(e) => setForm((f) => ({ ...f, color: e.target.value }))} className="h-8 w-10 rounded bg-transparent border border-slate-700" />
              </div>
            </Field>
            <Button data-testid="role-form-save" type="submit" disabled={saving} className="w-full cx-btn-primary rounded-xl h-11">{saving ? "Kaydediliyor..." : "Kaydet"}</Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
